import type { Dispatch, SetStateAction } from 'react';
import * as React from 'react';

import EoiWidgetContainer, {
  EmptyEoiWidgetContainer,
} from 'bundles/expression-of-interest/components/xddp/EoiWidgetContainer';
import { ACTION_TYPES, PRODUCT_TYPE } from 'bundles/expression-of-interest/constants';
import type { ExpressionOfInterestTextProps } from 'bundles/expression-of-interest/types';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';
import type { ChecklistEnrollmentFragment } from 'bundles/premium-certificate/__generated__/contentfulTypes';
import BoomerangFlow from 'bundles/premium-certificate/components/mastertrack/enrollment/BoomerangFlow';
import ChecklistFlow from 'bundles/premium-certificate/components/mastertrack/enrollment/ChecklistFlow';
import EnrollModalFlow from 'bundles/premium-certificate/components/mastertrack/enrollment/EnrollModalFlow';
import ExternalLinkFlow from 'bundles/premium-certificate/components/mastertrack/enrollment/ExternalLinkFlow';
import { ENROLLMENT_FLOW } from 'bundles/premium-certificate/constants';
import type { EnrollmentChecklistItem } from 'bundles/premium-certificate/constants/types';
import type { EnrollmentFlowProps } from 'bundles/premium-certificate/types';
import { trackEmailMeInfoClick } from 'bundles/premium-certificate/utils';

type Props = {
  slug?: Maybe<string>;
  mastertrackId?: string;
  certificateName: string;
  partnerName?: Maybe<string>;
  partnerMonotoneLogo?: Maybe<string>;
  expressionOfInterestContainer?: ExpressionOfInterestTextProps;
  isScrollPastBanner: boolean;
  setEmbeddedRegFormDisplayed: Dispatch<SetStateAction<boolean>>;
  certificateType?: Maybe<string>;
} & ChecklistEnrollmentFragment;

export const NoEnrollmentFlow = ({
  actionBarData,
  slug,
  mastertrackId,
  certificateName,
  certificateType,
  expressionOfInterestContainer,
}: EnrollmentFlowProps) => (
  <div data-test="NoEnrollmentFlow">
    <EoiWidgetContainer
      slug={slug}
      degreeId={mastertrackId || ''}
      actionBarData={actionBarData}
      programInterestText={expressionOfInterestContainer?.programInterestText || ''}
      emailMeInfoButtonData={{
        actionType: ACTION_TYPES.emailMeInfo,
        doAction: () => trackEmailMeInfoClick(slug, mastertrackId, certificateName, certificateType),
      }}
    />
  </div>
);

const CertificateEnrollmentFlow = ({
  slug,
  mastertrackId,
  expressionOfInterestContainer,
  partnerName,
  certificateName,
  partnerMonotoneLogo,
  isScrollPastBanner,
  setEmbeddedRegFormDisplayed,
  enrollmentChecklistHeading,
  enrollmentChecklistItemsCollection,
  certificateType,
}: Props) => {
  if (!expressionOfInterestContainer?.productEnrollmentFlow || !slug) {
    return <EmptyEoiWidgetContainer />;
  }

  const productType = PRODUCT_TYPE.mastertrack;
  const actionBarData = {
    isFixed: isScrollPastBanner,
    partnerName,
    programName: certificateName,
    partnerMonotoneLogo,
  };

  switch (expressionOfInterestContainer.productEnrollmentFlow) {
    case ENROLLMENT_FLOW.externalLink:
      return (
        <ExternalLinkFlow
          slug={slug}
          productType={productType}
          mastertrackId={mastertrackId}
          certificateName={certificateName}
          certificateType={certificateType}
          actionBarData={actionBarData}
          expressionOfInterestContainer={expressionOfInterestContainer}
          setEmbeddedRegFormDisplayed={setEmbeddedRegFormDisplayed}
        />
      );
    case ENROLLMENT_FLOW.paidOnCoursera:
      return (
        <EnrollModalFlow
          slug={slug}
          productType={productType}
          actionBarData={actionBarData}
          expressionOfInterestContainer={expressionOfInterestContainer}
          setEmbeddedRegFormDisplayed={setEmbeddedRegFormDisplayed}
          id={mastertrackId}
          certificateName={certificateName}
          certificateType={certificateType}
        />
      );
    case ENROLLMENT_FLOW.boomerang:
      return (
        <BoomerangFlow
          slug={slug}
          productType={productType}
          actionBarData={actionBarData}
          expressionOfInterestContainer={expressionOfInterestContainer}
          setEmbeddedRegFormDisplayed={setEmbeddedRegFormDisplayed}
          id={mastertrackId}
          certificateName={certificateName}
          certificateType={certificateType}
        />
      );
    case ENROLLMENT_FLOW.checklist:
      return (
        <ChecklistFlow
          slug={slug}
          productType={productType}
          actionBarData={actionBarData}
          expressionOfInterestContainer={expressionOfInterestContainer}
          setEmbeddedRegFormDisplayed={setEmbeddedRegFormDisplayed}
          enrollmentChecklistHeading={enrollmentChecklistHeading}
          enrollmentChecklistItems={enrollmentChecklistItemsCollection?.items as Array<EnrollmentChecklistItem>}
          mastertrackId={mastertrackId}
          certificateName={certificateName}
          certificateType={certificateType}
        />
      );
    case ENROLLMENT_FLOW.noEnrollment:
      return (
        <NoEnrollmentFlow
          slug={slug}
          productType={productType}
          actionBarData={actionBarData}
          expressionOfInterestContainer={expressionOfInterestContainer}
          setEmbeddedRegFormDisplayed={setEmbeddedRegFormDisplayed}
          mastertrackId={mastertrackId}
          certificateName={certificateName}
          certificateType={certificateType}
        />
      );
    default:
      return <EmptyEoiWidgetContainer />;
  }
};

export default CertificateEnrollmentFlow;
